import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import Turnstile from 'components/Turnstile';
import UserBox from 'components/UserBox';
import Api from 'services/Api';
import { isLoading } from 'lib/redux-utils';
import * as Router from 'modules/Router';
import Auth from 'modules/Auth';
import amplitude from 'lib/analytics';
import { email as validateEmail, password as validatePassword } from 'lib/valFunctors';
import { validatePasswordConfirm } from 'lib/valFuncs';
import { passwordErrorMessage } from 'lib/messages';
import ResetPasswordForm from './ResetPasswordForm';
import ResetPasswordRequestForm from './ResetPasswordRequestForm';
import './resetPassword.css';

const disableTurnstileCookie = Cookies.get('turnstile_exclude') === 'true';
const isTurnstileEnabled = !disableTurnstileCookie && __ENV__.CF_TURNSTILE_SITE_KEY;

export class _ResetPassword extends Component {
  constructor(props) {
    super(props);
    this.turnstileRef = React.createRef();
    this.state = {
      instructions: 'You will shortly receive an email with instructions to reset your password',
      token: props.token,
      isAwaitingChallenge: false,
      userRef: props.userRef,
      username: props.username,
      requestResetDone: false
    };
    this.resetPasswordFields = {
      username: {
        initial: this.state.username,
        hidden: true
      },
      password: {
        required: true,
        onChange: this.props.onChangePassword,
        error: passwordErrorMessage
      },
      passwordConfirm: {
        required: true,
        error: "Passwords don't match",
        onChange: validatePasswordConfirm
      }
    };
    this.resetPasswordRequestFields = {
      email: {
        required: true,
        onChange: validateEmail
      }
    };
  }

  componentDidMount() {
    this.mounted = true;
    const { token, userRef, username, location, stripSearch } = this.props;
    if (token || userRef || username) {
      stripSearch(location);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  submitResetRequestWithChallenge = async (data) => {
    let challengeToken;
    if (isTurnstileEnabled) {
      this.setState({ isAwaitingChallenge: true });
      try {
        challengeToken = await this.turnstileRef.current.getResponsePromise();
      } catch {
        // Always submit to BE regardless of Turnstile failing
        await this.submitResetRequest(data, challengeToken);
        throw new Error('Turnstile Challenge Timeout Error');
      } finally {
        this.setState({ isAwaitingChallenge: false });
      }
    }
    // Just in case __ENV__.CF_TURNSTILE_SITE_KEY isn't defined
    await this.submitResetRequest(data, challengeToken);
  };

  submitResetRequest = async (data, challenge) => {
    this.setState({ requestResetDone: false });
    const res = await this.props.resetRequest(data.email, challenge);
    if (this.mounted) {
      this.setState({ requestResetDone: true });
      this.setState({ instructions: res.msg });
    }
    return true;
  };

  submitReset = async (data) => {
    const res = await this.props.reset(
      data.password,
      data.passwordConfirm,
      this.state.userRef,
      this.state.token
    );
    if (this.mounted) {
      this.setState({ instructions: res.msg });
      amplitude.track('Password Reset');
    }
    this.props.redirectLoginMessage();
  };

  onSubmitResetRequestError = () => {
    if (isTurnstileEnabled) {
      this.turnstileRef.current.reset();
    }
  };

  render() {
    const { token, requestResetDone, instructions } = this.state;
    const { loading } = this.props;

    return (
      <UserBox
        id="loginBox"
        className="userBox--white resetPassword"
        title="Reset your password"
        overScroll
      >
        {token ? (
          <ResetPasswordForm
            fields={this.resetPasswordFields}
            submit={this.submitReset}
            loading={loading}
          />
        ) : (
          <>
            <ResetPasswordRequestForm
              loading={loading || this.state.isAwaitingChallenge}
              instructions={instructions}
              requestResetDone={requestResetDone}
              fields={this.resetPasswordRequestFields}
              submit={this.submitResetRequestWithChallenge}
              onError={this.onSubmitResetRequestError}
            />
            {isTurnstileEnabled ? (
              <Turnstile ref={this.turnstileRef} action="reset-password" />
            ) : null}
          </>
        )}
      </UserBox>
    );
  }
}

_ResetPassword.propTypes = {
  loading: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  token: PropTypes.string,
  userRef: PropTypes.string,
  username: PropTypes.string,
  resetRequest: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  stripSearch: PropTypes.func.isRequired,
  onChangePassword: PropTypes.func.isRequired,
  redirectLoginMessage: PropTypes.func.isRequired
};

_ResetPassword.defaultProps = {
  token: null,
  userRef: null,
  username: null
};

const mapStateToProps = (state, { location: { search } }) => {
  const { token, userRef, username } = Object.fromEntries(new URLSearchParams(search).entries());
  return {
    loading: isLoading(state, [Auth.AT.RESET_PASSWORD_REQUEST._, Auth.AT.RESET_PASSWORD._]),
    token,
    userRef,
    username
  };
};

const mapDispatchToProps = (dispatch) => ({
  resetRequest: async (email, challengeToken) =>
    Api.actions.auth.resetPasswordRequest(null, { email, challenge: challengeToken })(
      dispatch,
      true
    ),
  reset: (newPassword, newPasswordConfirm, userRef, resetToken) =>
    Api.actions.auth.resetPassword(null, {
      newPassword,
      newPasswordConfirm,
      userRef,
      resetToken
    })(dispatch, true),
  stripSearch: (location) => dispatch(Router.actions.stripSearch(location)),
  onChangePassword: validatePassword(dispatch),
  redirectLoginMessage: () =>
    dispatch(Router.actions.redirectLoginMessage('Password reset successfully'))
});

export default connect(mapStateToProps, mapDispatchToProps)(_ResetPassword);
